import React, { FC, useMemo } from "react"
import {
  Typography,
  Stack,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
  CloseIcon,
  Button,
  Link
} from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl } from "react-intl"
import { EMDTableContainer } from "./EMDTable"
import { OOPMTableContainer } from "./OOPMTable"
import { DbgPaths } from "../../../../utils/Paths"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { useBenefitEnrollment } from "#/components/context/enrollment/BenefitEnrollmentContext"

type ExternalDeductibleAndOopmInformationalProps = { onClose: () => void }

const ExternalDeductibleAndOopmInformational: FC<ExternalDeductibleAndOopmInformationalProps> = ({ onClose }) => {
  const { selectedTradingPartnerPk } = useCurrentUser()
  const { availableTradingPartners } = useBenefitEnrollment()
  const intl = useIntl()

  const tradingPartnerName = useMemo(() => {
    if (!selectedTradingPartnerPk || !availableTradingPartners) {
      return "(Error determining carrier)"
    }
    return (
      availableTradingPartners.find((tp) => tp.tradingPartnerPk === selectedTradingPartnerPk)?.description ||
      "(Error determining carrier)"
    )
  }, [selectedTradingPartnerPk, availableTradingPartners])

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <IconButton aria-label={intl.formatMessage({ defaultMessage: "close" })} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogTitle id="deductible-oopm-modal-header">
        <FormattedMessage defaultMessage="Deductible and out of pocket maximum" />
      </DialogTitle>
      <DialogContent>
        <Stack display="flex" gap={(theme) => theme.tokens.spacing.md}>
          <Typography>
            <FormattedMessage
              defaultMessage="Always check your {carrierName} portal for the most updated insurance information. Claim processing times vary by insurance provider. If needed, you can <link>update your insurance details</link> anytime."
              values={{
                carrierName: tradingPartnerName,
                link: (linkContent: React.ReactNode) => (
                  <Link href="/health-insurance-details" color="inherit">
                    {linkContent}
                  </Link>
                )
              }}
            />
          </Typography>
          <Typography sx={(theme) => ({ marginBlockEnd: theme.spacing(theme.tokens.spacing.xxxl) })}>
            <FormattedMessage defaultMessage="Carrot funds you use may also be subject to bank fees, taxes, and other plan details." />
          </Typography>

          <Typography variant="body1" fontWeight="bold" color={(theme) => theme.palette.text.secondary}>
            <FormattedMessage defaultMessage="Deductible" />
          </Typography>
          <Typography>
            <FormattedMessage
              defaultMessage="Once you reach your health insurance deductible, you can use Carrot funds for eligible <link>qualified medical expenses</link> (QMEs) (in this category)."
              values={{
                link: (linkContent) => (
                  <Link href={DbgPaths.QUALIFIED_MEDICAL_EXPENSES} color={"inherit"} target="_blank">
                    {linkContent}
                  </Link>
                )
              }}
            />
          </Typography>
          <Typography>
            <FormattedMessage
              defaultMessage="<link>Learn more about deductibles</link>"
              values={{
                link: (linkContent) => (
                  <Link href={DbgPaths.DEDUCTIBLES} color={"inherit"} target="_blank">
                    {linkContent}
                  </Link>
                )
              }}
            />
          </Typography>
          <EMDTableContainer showRemainingAsterisk={false} />

          <Typography
            variant="body1"
            fontWeight="bold"
            color={(theme) => theme.palette.text.secondary}
            sx={(theme) => ({ paddingBlockStart: theme.spacing(theme.tokens.spacing.md) })}
          >
            <FormattedMessage defaultMessage="Out of pocket maximum" />
          </Typography>
          <Typography>
            <FormattedMessage defaultMessage="Your health insurance out-of-pocket maximum (OOPM) status may impact your cost share for eligible expenses (in this category)." />
          </Typography>
          <OOPMTableContainer />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          <FormattedMessage defaultMessage="Close" />
        </Button>
      </DialogActions>
    </>
  )
}

export { ExternalDeductibleAndOopmInformational }
